import { sharedSchema } from "@shared-database/pgSchema";
import { EntityTypeEnumValues } from "@shared-database/enums";
import { KnowledgeGraph } from "@shared-database/models/rag/knowledge-graph";
import {
  leoEnum,
  leoID,
  leoNotNullForeignID,
  timestampColumns,
} from "@shared-database/utils";
import { sql, type InferInsertModel, type InferSelectModel } from "drizzle-orm";
import {
  index,
  integer,
  real,
  text,
  timestamp,
  uniqueIndex,
  vector,
} from "drizzle-orm/pg-core";

import { EMBEDDING_DIMENSIONS } from "@shared/utils/rag/constants/extraction";

export const entityTypeEnum = leoEnum("EntityType", EntityTypeEnumValues);

/**
 * Knowledge-graph-scoped entities. Deduplicated across all files within
 * a graph by (knowledgeGraphId, normalizedName, type). Per-file provenance is
 * tracked in KnowledgeEntitySource.
 *
 * `name` is the human-readable display label (e.g. "Next.js") and is kept as
 * the best-seen variant. `normalizedName` is the canonical form used for
 * dedup: lowercased with non-alphanumerics stripped (e.g. "nextjs"), so that
 * surface variants like "Next.js", "nextjs" and "Next-JS" collapse into the
 * same row.
 *
 * `type` is the primary entity type (majority vote within a single
 * extraction run). It participates in the unique key so that genuinely
 * distinct identities sharing a name — "Apple" (COMPANY) vs "Apple"
 * (FRUIT) — live as separate rows, matching LightRAG's type-aware
 * canonicalisation. `types` still accumulates every alternate type the
 * LLM has assigned to a given identity so colouring / filtering can see
 * the full set without fragmenting the row.
 */
export const KnowledgeEntity = sharedSchema.table(
  "KnowledgeEntity",
  {
    // Mixins
    ...timestampColumns,

    // Properties
    id: leoID("id"),
    knowledgeGraphId: leoNotNullForeignID("knowledgeGraphId").references(
      () => KnowledgeGraph.id,
      { onDelete: "cascade" },
    ),
    name: text("name").notNull(),
    normalizedName: text("normalizedName").notNull(),
    type: entityTypeEnum("type").notNull(),
    types: entityTypeEnum("types")
      .array()
      .notNull()
      .default(sql`ARRAY[]::"EntityType"[]`),
    description: text("description").notNull(),
    updatedAt: timestamp("updatedAt", { mode: "date" }).defaultNow().notNull(),

    /**
     * First-seen / last-seen timestamps driven by the extraction pipeline.
     * Set at INSERT and refreshed (`lastSeenAt` only) on every re-extraction.
     * Together with `degree` / `rank` these power temporal and salience
     * filters in the knowledge-graph UI.
     */
    firstSeenAt: timestamp("firstSeenAt", { mode: "date" }).notNull(),
    lastSeenAt: timestamp("lastSeenAt", { mode: "date" }).notNull(),

    /**
     * Degree (number of neighbours in the profile graph) and an
     * optional LLM-assigned salience rank. Precomputed by community
     * detection so the UI can sort / filter without scanning relations
     * on every render.
     */
    degree: integer("degree"),
    rank: real("rank"),

    // NOTE: an HNSW cosine index on `embedding`
    // (`KnowledgeEntity_embedding_hnsw_idx`) exists in the hand-written
    // migration; drizzle-kit has no `hnsw` builder, so we deliberately
    // don't declare it here.
    embedding: vector("embedding", { dimensions: EMBEDDING_DIMENSIONS }),
  },
  (table) => [
    uniqueIndex("KnowledgeEntity_uniqueIdx").on(
      table.knowledgeGraphId,
      table.normalizedName,
      table.type,
    ),
    index("KnowledgeEntity_knowledgeGraphIdx").on(table.knowledgeGraphId),
    index("KnowledgeEntity_nameIdx").on(table.name),
  ],
);

// Types
export type KnowledgeEntityType = InferSelectModel<typeof KnowledgeEntity>;
export type KnowledgeEntityInsertType = InferInsertModel<
  typeof KnowledgeEntity
>;
